import { useState, useEffect } from 'react';

function KittyAvatar({ x, y, direction, isWalking }: { x: number; y: number; direction: 'left' | 'right'; isWalking: boolean }) {
  return (
    <div
      style={{
        position: 'absolute',
        left: x,
        top: y,
        transform: direction === 'left' ? 'scaleX(-1)' : 'scaleX(1)',
        transition: 'left 0.05s, top 0.05s',
        zIndex: 100
      }}
    >
      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        @keyframes tailWag {
          0%, 100% { transform: rotate(-20deg); }
          50% { transform: rotate(20deg); }
        }
        @keyframes earTwitch {
          0%, 90%, 100% { transform: rotate(0deg); }
          95% { transform: rotate(10deg); }
        }
        .walking { animation: bounce 0.2s ease-in-out infinite; }
        .tail { animation: tailWag 0.3s ease-in-out infinite; }
        .ear { animation: earTwitch 2s ease-in-out infinite; }
      `}</style>

      <svg
        width="80"
        height="100"
        viewBox="0 0 80 100"
        className={isWalking ? 'walking' : ''}
      >
        {/* Tail */}
        <path
          className={isWalking ? 'tail' : ''}
          d="M 15 70 Q 0 60 5 50 Q 10 45 15 50"
          fill="none"
          stroke="#FFB6C1"
          strokeWidth="4"
          strokeLinecap="round"
          style={{ transformOrigin: '15px 70px' }}
        />

        {/* Body */}
        <ellipse cx="40" cy="75" rx="20" ry="15" fill="#FFB6C1" />

        {/* Dress */}
        <path d="M 25 70 L 20 90 L 60 90 L 55 70 Z" fill="#FF69B4" />
        <path d="M 30 70 L 28 85 L 52 85 L 50 70 Z" fill="#FFB6C1" />

        {/* Legs */}
        <rect x="30" y="85" width="6" height="12" rx="3" fill="#FFE4E1" />
        <rect x="44" y="85" width="6" height="12" rx="3" fill="#FFE4E1" />

        {/* Shoes */}
        <ellipse cx="33" cy="97" rx="5" ry="3" fill="#FF1493" />
        <ellipse cx="47" cy="97" rx="5" ry="3" fill="#FF1493" />

        {/* Head */}
        <circle cx="40" cy="40" r="25" fill="#FFE4E1" />

        {/* Ears */}
        <g className="ear" style={{ transformOrigin: '22px 20px' }}>
          <polygon points="18,25 12,5 28,18" fill="#FFB6C1" />
          <polygon points="20,22 16,10 25,19" fill="#FFC0CB" />
        </g>
        <g className="ear" style={{ transformOrigin: '58px 20px', animationDelay: '0.5s' }}>
          <polygon points="62,25 68,5 52,18" fill="#FFB6C1" />
          <polygon points="60,22 64,10 55,19" fill="#FFC0CB" />
        </g>

        {/* Hair */}
        <ellipse cx="40" cy="22" rx="22" ry="10" fill="#8B4513" />
        <ellipse cx="25" cy="35" rx="8" ry="15" fill="#8B4513" />
        <ellipse cx="55" cy="35" rx="8" ry="15" fill="#8B4513" />

        {/* Face */}
        {/* Eyes */}
        <ellipse cx="32" cy="42" rx="6" ry="7" fill="white" />
        <ellipse cx="48" cy="42" rx="6" ry="7" fill="white" />
        <circle cx="33" cy="43" r="4" fill="#4169E1" />
        <circle cx="49" cy="43" r="4" fill="#4169E1" />
        <circle cx="34" cy="42" r="1.5" fill="white" />
        <circle cx="50" cy="42" r="1.5" fill="white" />

        {/* Blush */}
        <ellipse cx="24" cy="50" rx="5" ry="3" fill="#FFB6C1" opacity="0.6" />
        <ellipse cx="56" cy="50" rx="5" ry="3" fill="#FFB6C1" opacity="0.6" />

        {/* Nose */}
        <ellipse cx="40" cy="48" rx="2" ry="1.5" fill="#FFB6C1" />

        {/* Mouth */}
        <path d="M 36 52 Q 40 56 44 52" fill="none" stroke="#FF69B4" strokeWidth="1.5" strokeLinecap="round" />

        {/* Whiskers */}
        <line x1="20" y1="46" x2="28" y2="48" stroke="#DDD" strokeWidth="0.5" />
        <line x1="20" y1="50" x2="28" y2="50" stroke="#DDD" strokeWidth="0.5" />
        <line x1="52" y1="48" x2="60" y2="46" stroke="#DDD" strokeWidth="0.5" />
        <line x1="52" y1="50" x2="60" y2="50" stroke="#DDD" strokeWidth="0.5" />
      </svg>
    </div>
  );
}

export default function HomePage() {
  const [position, setPosition] = useState({ x: 100, y: 300 });
  const [direction, setDirection] = useState<'left' | 'right'>('right');
  const [isWalking, setIsWalking] = useState(false);
  const [keys, setKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'w', 'a', 's', 'd'].includes(e.key)) {
        e.preventDefault();
        setKeys(prev => new Set(prev).add(e.key.toLowerCase()));
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      setKeys(prev => {
        const next = new Set(prev);
        next.delete(e.key.toLowerCase());
        return next;
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  useEffect(() => {
    const speed = 8;
    let animationFrame: number;

    const move = () => {
      let dx = 0;
      let dy = 0;

      if (keys.has('arrowleft') || keys.has('a')) { dx -= speed; setDirection('left'); }
      if (keys.has('arrowright') || keys.has('d')) { dx += speed; setDirection('right'); }
      if (keys.has('arrowup') || keys.has('w')) dy -= speed;
      if (keys.has('arrowdown') || keys.has('s')) dy += speed;

      setIsWalking(dx !== 0 || dy !== 0);

      if (dx !== 0 || dy !== 0) {
        setPosition(prev => ({
          x: Math.max(0, Math.min(window.innerWidth - 80, prev.x + dx)),
          y: Math.max(0, Math.min(window.innerHeight - 100, prev.y + dy))
        }));
      }

      animationFrame = requestAnimationFrame(move);
    };

    animationFrame = requestAnimationFrame(move);
    return () => cancelAnimationFrame(animationFrame);
  }, [keys]);

  return (
    <div className="animated-bg" style={{
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      fontSize: '3rem',
      fontFamily: 'system-ui, sans-serif',
      color: 'white',
      overflow: 'hidden'
    }}>
      <style>{`
        @keyframes gradientShift {
          0%, 100% { background: linear-gradient(135deg, #FF69B4 0%, #FFB6C1 25%, #DDA0DD 50%, #BA55D3 75%, #9932CC 100%); }
          25% { background: linear-gradient(135deg, #FFB6C1 0%, #FF69B4 25%, #BA55D3 50%, #9932CC 75%, #8B008B 100%); }
          50% { background: linear-gradient(135deg, #9932CC 0%, #BA55D3 25%, #DDA0DD 50%, #FFB6C1 75%, #FF69B4 100%); }
          75% { background: linear-gradient(135deg, #8B008B 0%, #9932CC 25%, #BA55D3 50%, #FF69B4 75%, #FFB6C1 100%); }
        }
        .animated-bg {
          animation: gradientShift 8s ease-in-out infinite;
          background-size: 400% 400%;
        }
        @keyframes flash {
          0%, 100% { filter: drop-shadow(0 0 20px gold) drop-shadow(0 0 40px yellow); }
          50% { filter: drop-shadow(0 0 50px gold) drop-shadow(0 0 80px yellow) drop-shadow(0 0 120px orange); }
        }
        .flashing-star {
          animation: flash 1.5s ease-in-out infinite;
        }
      `}</style>

      <div
        className="flashing-star"
        style={{
          position: 'absolute',
          top: '0',
          left: '0',
          width: '50vw',
          height: '50vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          preserveAspectRatio="xMidYMid meet"
          style={{ position: 'absolute' }}
        >
          <polygon
            points="50,5 61,35 95,35 68,57 79,91 50,70 21,91 32,57 5,35 39,35"
            fill="gold"
            stroke="orange"
            strokeWidth="1"
          />
        </svg>
        <img
          src="/shayla-supreme-photo.jpeg"
          alt="Shayla Supreme"
          style={{
            width: '85%',
            height: '85%',
            objectFit: 'cover',
            objectPosition: 'center top',
            clipPath: 'polygon(50% 5%, 61% 35%, 95% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 5% 35%, 39% 35%)',
            position: 'relative',
            zIndex: 1
          }}
        />
      </div>

      <KittyAvatar x={position.x} y={position.y} direction={direction} isWalking={isWalking} />

      <div style={{ marginLeft: '25vw', textAlign: 'center' }}>
        <div>Hello from Shayla!</div>
        <div style={{ fontSize: '1rem', marginTop: '20px', opacity: 0.8 }}>
          Use arrow keys or WASD to move the kitty girl
        </div>
      </div>
    </div>
  );
}
